import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, UserCheck, UserX, ShieldOff, ClipboardList } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function OrgBulkAssignPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [form, setForm] = useState({
    course_id: params.get("course") || "",
    assignment_type: "all_staff",
    department_id: "",
    designation_id: "",
    due_date: "",
    mandatory_or_optional: "optional",
  });
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, d, dg] = await Promise.all([
          api.get("/org/course-mappings"),
          api.get("/org/departments"),
          api.get("/org/designations"),
        ]);
        setCourses(c.data.data.mappings.map((m) => m.course));
        setDepts(d.data.data.departments);
        setDesigs(dg.data.data.designations);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const submitPreview = async () => {
    if (!form.course_id) return toast.error("Select a course.");
    if (form.assignment_type === "department" && !form.department_id) return toast.error("Select a department.");
    if (form.assignment_type === "designation" && !form.designation_id) return toast.error("Select a designation.");
    setPreviewing(true); setResult(null);
    try {
      const body = {
        course_id: form.course_id,
        assignment_type: form.assignment_type,
        user_ids: [],
        department_id: form.assignment_type === "department" ? form.department_id : null,
        designation_id: form.assignment_type === "designation" ? form.designation_id : null,
        mandatory_or_optional: form.mandatory_or_optional,
      };
      const { data } = await api.post("/org/course-assignments/bulk-preview", body);
      setPreview(data.data);
      setSelected(new Set(data.data.new_assignments.map((u) => u.id)));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPreviewing(false); }
  };

  const submitConfirm = async () => {
    if (selected.size === 0) return toast.error("No staff selected.");
    setConfirming(true);
    try {
      const body = {
        course_id: form.course_id,
        assignment_type: form.assignment_type,
        user_ids: Array.from(selected),
        mandatory_or_optional: form.mandatory_or_optional,
      };
      if (form.due_date) body.due_date = form.due_date;
      const { data } = await api.post("/org/course-assignments/bulk-confirm", body);
      setResult(data.data);
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConfirming(false); }
  };

  const reset = () => { setPreview(null); setResult(null); setSelected(new Set()); };

  return (
    <DashboardLayout title="Bulk course assignment" breadcrumbs={["My Institute", "Bulk assign"]}>
      <div className="space-y-6" data-testid="org-bulk-assign">
        {!preview && !result && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Configure assignment</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2">
                <Label>Course *</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="bulk-course-select"><SelectValue placeholder="Select mapped course" /></SelectTrigger>
                  <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Assign to *</Label>
                <Select value={form.assignment_type} onValueChange={(v) => setForm({ ...form, assignment_type: v })}>
                  <SelectTrigger data-testid="bulk-assign-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_staff">All staff</SelectItem>
                    <SelectItem value="department">A department</SelectItem>
                    <SelectItem value="designation">A designation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.assignment_type === "department" && (
                <div className="space-y-1">
                  <Label>Department *</Label>
                  <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                    <SelectTrigger data-testid="bulk-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.department_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.assignment_type === "designation" && (
                <div className="space-y-1">
                  <Label>Designation *</Label>
                  <Select value={form.designation_id} onValueChange={(v) => setForm({ ...form, designation_id: v })}>
                    <SelectTrigger data-testid="bulk-desig-select"><SelectValue placeholder="Select designation" /></SelectTrigger>
                    <SelectContent>{desigs.map((d) => <SelectItem key={d.id} value={d.id}>{d.designation_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1"><Label>Due date (optional)</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div className="space-y-1">
                <Label>Priority</Label>
                <Select value={form.mandatory_or_optional} onValueChange={(v) => setForm({ ...form, mandatory_or_optional: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="optional">Optional</SelectItem>
                    <SelectItem value="mandatory">Mandatory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button onClick={submitPreview} disabled={previewing} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="bulk-preview-btn">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                Preview eligibility
              </button>
            </div>
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <Card title="Total eligible" value={preview.total_eligible} tone="navy" />
              <Card title="New assignments" value={preview.new_assignments.length} tone="green" />
              <Card title="Already assigned" value={preview.already_assigned.length} tone="slate" />
              <Card title="Skipped (inactive+suspended)" value={preview.inactive.length + preview.suspended.length} tone="amber" />
            </div>

            {(preview.suspended.length > 0 || preview.inactive.length > 0) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <span className="font-bold">Skipped:</span> {preview.suspended.length} suspended and {preview.inactive.length} inactive staff will be excluded. Only active staff can be enrolled.
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 flex items-center justify-between">
                <span>Preview ({selected.size} selected)</span>
                <div className="flex items-center gap-2 text-xs">
                  <button onClick={() => setSelected(new Set(preview.new_assignments.map((u) => u.id)))} className="rounded border border-slate-200 bg-white px-2 py-1 hover:border-[#101876]/40">Select new</button>
                  <button onClick={() => setSelected(new Set())} className="rounded border border-slate-200 bg-white px-2 py-1 hover:border-slate-400">Clear</button>
                </div>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Staff</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Emp. ID</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Designation</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Group</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {preview.new_assignments.map((u) => (
                    <TableRow key={u.id} className="bg-emerald-50/40">
                      <TableCell><input type="checkbox" checked={selected.has(u.id)} onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(u.id); else n.delete(u.id); return n; })} data-testid={`bulk-select-${u.id}`} /></TableCell>
                      <TableCell>
                        <div className="font-semibold text-slate-900">{u.full_name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{u.employee_id || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-600">{u.designation || "—"}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><UserCheck className="h-3.5 w-3.5" /> New</span></TableCell>
                    </TableRow>
                  ))}
                  {preview.already_assigned.map((u) => (
                    <TableRow key={`al-${u.id}`} className="bg-slate-50">
                      <TableCell></TableCell>
                      <TableCell><div className="text-sm text-slate-500 line-through">{u.full_name}</div></TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">{u.employee_id || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-400">{u.designation || "—"}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><CheckCircle2 className="h-3.5 w-3.5" /> Already assigned</span></TableCell>
                    </TableRow>
                  ))}
                  {preview.suspended.map((u) => (
                    <TableRow key={`s-${u.id}`} className="bg-rose-50/40">
                      <TableCell></TableCell>
                      <TableCell><div className="text-sm text-slate-500">{u.full_name}</div></TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">{u.employee_id || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-400">{u.designation || "—"}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700"><ShieldOff className="h-3.5 w-3.5" /> Suspended</span></TableCell>
                    </TableRow>
                  ))}
                  {preview.inactive.map((u) => (
                    <TableRow key={`i-${u.id}`} className="bg-slate-50">
                      <TableCell></TableCell>
                      <TableCell><div className="text-sm text-slate-500">{u.full_name}</div></TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">{u.employee_id || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-400">{u.designation || "—"}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><UserX className="h-3.5 w-3.5" /> Inactive</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
              <button onClick={reset} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Start over</button>
              <button onClick={submitConfirm} disabled={confirming || selected.size === 0} className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-semibold text-white hover:bg-[#256a0d] disabled:opacity-70" data-testid="bulk-confirm-btn">
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Assign {selected.size} staff
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h3 className="mt-3 text-lg font-bold text-emerald-900">Assignment complete</h3>
            <p className="mt-1 text-sm text-emerald-800"><span className="font-bold">{result.created}</span> staff assigned · <span className="font-bold">{result.skipped}</span> skipped</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button onClick={reset} className="rounded-md border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50">Assign another</button>
              <button onClick={() => navigate("/app/hospital/course-assignments")} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]">View assignments</button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Card({ title, value, tone }) {
  const map = { navy: "border-[#101876]/30 bg-white", green: "border-emerald-200 bg-emerald-50", slate: "border-slate-200 bg-white", amber: "border-amber-200 bg-amber-50" };
  return (
    <div className={`rounded-2xl border p-4 ${map[tone]}`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</div>
      <div className="mt-1 text-3xl font-black text-slate-900">{value}</div>
    </div>
  );
}
